import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import DatePickerComponent from '../common/DatePickerComponent';
import noImage from '../../assets/No-Image-Placeholder.png';

const getDisplayLocation = (row) => {
    if (Array.isArray(row.Locations) && row.Locations.length) {
        return row.Locations.find((l) => l.suggestedLocationId) || row.Locations[0];
    }
    // Legacy flat fields fallback
    return {
        warehouse: row.WarehouseCode || row.Warehouse || '',
        area: row.AreaCode || row.Area || '',
        row: row.RowCode || row.Row || '',
        bay: row.BayCode || row.Bay || '',
        storageId: row.StorageId || '',
    };
};


const PickItemCard = ({ item, onPick, onCheck, onPreview, hideSelection }) => {
    const loc = getDisplayLocation(item);
    const picked = item.PickedQuantity || 0;
    const total = item.TotalQuantity || 0;
    const fullyPicked = picked >= total;

    const handleCardTap = (e) => {
        // Don't pick if user tapped checkbox / thumbnail
        if (e.target.closest('.mpp-card-checkbox') || e.target.closest('.mpp-thumb')) {
            return;
        }
        // Selection is hidden on the Picked tab — card tap is a no-op there.
        if (hideSelection) return;
        // Toggle checkbox selection instead of directly picking
        if (typeof onCheck === 'function') {
            onCheck({ target: { checked: !item.isChecked } }, item);
        }
    };

    return (
        <button
            type="button"
            className={`mpp-card ${fullyPicked ? 'mpp-card--picked' : ''}`}
            onClick={handleCardTap}
        >
            <div className="mpp-card-row">
                {!hideSelection && (
                    <span className="mpp-card-checkbox">
                        <input
                            type="checkbox"
                            checked={!!item.isChecked}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => onCheck?.(e, item)}
                        />
                    </span>
                )}
                <button
                    type="button"
                    className="mpp-thumb"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (typeof onPreview === 'function') onPreview(item);
                    }}
                    aria-label="Preview product image"
                >
                    {item.ThumbnailUrl ? (
                        <img src={item.ThumbnailUrl} alt={item.ProductName || item.ProductSKU} />
                    ) : (
                        <img src={noImage} alt="No product" />
                    )}
                </button>
                <div className="mpp-card-main">
                    <div className="mpp-product-name">
                        {item.ProductName || item.ProductSKU}
                    </div>
                    <div className="mpp-seller">{item.CompanySellerName || ''}</div>
                </div>
                <div className="mpp-qty-block">
                    <div className="mpp-qty-num">{total}</div>
                    <div className="mpp-qty-sub">{picked}/{total} picked</div>
                </div>
            </div>
            <div className="mpp-loc-row">
                <i className="fa fa-map-marker-alt mpp-loc-pin" aria-hidden="true"></i>
                {loc.warehouse && (
                    <span className="mpp-loc-chip-muted">{loc.warehouse}</span>
                )}
                {loc.area && (
                    <>
                        <span className="mpp-loc-sep">›</span>
                        <span className="mpp-loc-chip-muted">{loc.area}</span>
                    </>
                )}
                {loc.row && (
                    <>
                        <span className="mpp-loc-sep">›</span>
                        <span className="mpp-loc-chip-strong">{loc.row}</span>
                    </>
                )}
                {loc.bay && (
                    <>
                        <span className="mpp-loc-sep">›</span>
                        <span className="mpp-loc-chip-strong">{loc.bay}</span>
                    </>
                )}
                {loc.storageId && (
                    <>
                        <span className="mpp-loc-sep">›</span>
                        <span className="mpp-loc-chip-strong">{loc.storageId}</span>
                    </>
                )}
                {item.ProductSKU && (
                    <span className="mpp-sku-pill">{item.ProductSKU}</span>
                )}
            </div>
        </button>
    );
};

PickItemCard.propTypes = {
    item: PropTypes.object.isRequired,
    onPick: PropTypes.func,
    onCheck: PropTypes.func,
    onPreview: PropTypes.func,
    hideSelection: PropTypes.bool,
};


const MobilePickPlan = ({
    packingLabels,
    scanInput,
    setScanInput,
    scanInputRef,
    scanTimerRef,
    scanFeedback,
    processScan,
    activeTab,
    setActiveTab,
    handleCheckRecord,
    handleMultiUpdate,
    handleSectionPrintAllAction,
    filterData,
    handleDateChange,
    pickItem,
    onPreviewItem,
}) => {
    // Keep scan input always focused so the barcode scanner input is always
    // captured. Skip refocus when the user is interacting with another form
    // control (input/textarea/select/button) or inside a modal/dialog so
    // taps on cards, buttons, the date picker, and dialogs still work.
    useEffect(() => {
        const keepFocus = () => {
            const el = scanInputRef?.current;
            if (!el) return;
            const active = document.activeElement;
            if (active === el) return;
            if (active && active !== document.body) {
                const tag = (active.tagName || '').toUpperCase();
                if (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(tag)) return;
                if (typeof active.closest === 'function' && active.closest('.modal, [role="dialog"]')) return;
            }
            el.focus();
        };
        keepFocus();
        const interval = setInterval(keepFocus, 500);
        document.addEventListener('click', keepFocus);
        return () => {
            clearInterval(interval);
            document.removeEventListener('click', keepFocus);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pendingRows = packingLabels.filter((i) => (i.PickedQuantity || 0) < i.TotalQuantity);
    const pickedRows = packingLabels.filter((i) => (i.PickedQuantity || 0) >= i.TotalQuantity);
    const visibleRows = activeTab === 'pending' ? pendingRows : pickedRows;
    const pendingCount = pendingRows.reduce(
        (sum, i) => sum + ((i.TotalQuantity || 0) - (i.PickedQuantity || 0)),
        0
    );
    const pickedCount = pickedRows.reduce((sum, i) => sum + (i.PickedQuantity || 0), 0);
    const totalUnits = packingLabels.reduce((s, i) => s + (i.TotalQuantity || 0), 0);
    const pickedUnits = packingLabels.reduce((s, i) => s + (i.PickedQuantity || 0), 0);
    const progressPct = totalUnits ? Math.round((pickedUnits / totalUnits) * 100) : 0;
    const selectedCount = packingLabels.filter((i) => i.isChecked).length;
    const allSelected = packingLabels.length > 0 && selectedCount === packingLabels.length;
    const anySelected = selectedCount > 0;

    return (
        <main className="mobile-pick-plan">
            <div className="mpp-scan-wrapper">
                <div className="mpp-scan-box">
                    <i className="fa fa-th mpp-scan-icon" aria-hidden="true"></i>
                    <div className="mpp-scan-content">
                        <div className="mpp-scan-label">Scan or type SKU / bin label</div>
                        <input
                            ref={scanInputRef}
                            type="text"
                            className="mpp-scan-input"
                            value={scanInput}
                            onChange={(e) => {
                                const val = e.target.value;
                                setScanInput(val);
                                if (scanTimerRef?.current) {
                                    clearTimeout(scanTimerRef.current);
                                }
                                if (val.trim() && scanTimerRef) {
                                    scanTimerRef.current = setTimeout(() => processScan(val), 150);
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (scanTimerRef?.current) {
                                        clearTimeout(scanTimerRef.current);
                                    }
                                    processScan(scanInput);
                                }
                            }}
                            placeholder="tap to type manually if needed"
                            autoComplete="off"
                        />
                    </div>
                    <button
                        type="button"
                        className="mpp-scan-btn"
                        onClick={() => processScan(scanInput)}
                    >
                        SCAN
                    </button>
                </div>
            </div>

            {scanFeedback && (
                <div className={`pick-scan-feedback pick-scan-${scanFeedback.type} mpp-feedback`}>
                    <span className="pick-scan-icon">
                        {scanFeedback.type === 'success' && <i className="fa fa-check-circle"></i>}
                        {scanFeedback.type === 'error' && <i className="fa fa-times-circle"></i>}
                        {scanFeedback.type === 'warning' && <i className="fa fa-exclamation-triangle"></i>}
                    </span>
                    <span className="pick-scan-message">{scanFeedback.message}</span>
                </div>
            )}

            <div className="mpp-action-row">
                {activeTab !== 'scanned' && (
                    <button
                        type="button"
                        className="mpp-action-btn mpp-action-primary"
                        disabled={!anySelected}
                        onClick={handleMultiUpdate}
                    >
                        <i className="fa fa-check" aria-hidden="true"></i> Marked As Picked
                    </button>
                )}
                
                <span className="mpp-action-date">
                    <DatePickerComponent
                        dateField={filterData.startDate}
                        handleDateChange={(date) => handleDateChange(date)}
                        fieldName="ValidFrom"
                        dateFormat="MM-dd-yyyy"
                        showFutureDate={false}
                        adjustDateOnChange={false}
                    />
                </span>
            </div>

            <div className="mpp-progress-bar">
                <div
                    className="mpp-progress-fill"
                    style={{ width: `${progressPct}%` }}
                ></div>
            </div>

            {activeTab !== 'scanned' && (
                <div className="mpp-select-bar">
                    <label className="mpp-select-all">
                        <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={(e) => handleCheckRecord(e, undefined)}
                        />
                        <span>Select All</span>
                    </label>
                    <span className="mpp-select-count">{selectedCount} selected</span>
                </div>
            )}

            <div className="mpp-stats">
                <div className="mpp-stat mpp-stat-pending">
                    <div className="mpp-stat-num">{pendingCount}</div>
                    <div className="mpp-stat-label">PENDING</div>
                </div>
                <div className="mpp-stat mpp-stat-picked">
                    <div className="mpp-stat-num">{pickedCount}</div>
                    <div className="mpp-stat-label">PICKED</div>
                </div>
                <div className="mpp-stat mpp-stat-total">
                    <div className="mpp-stat-num">{totalUnits}</div>
                    <div className="mpp-stat-label">TOTAL</div>
                </div>
            </div>

            <div className="mpp-tabs">
                <button
                    type="button"
                    className={`mpp-tab ${activeTab === 'pending' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pending')}
                >
                    Pending <span className="mpp-tab-badge mpp-tab-badge-pending">{pendingCount}</span>
                </button>
                <button
                    type="button"
                    className={`mpp-tab ${activeTab === 'scanned' ? 'active' : ''}`}
                    onClick={() => setActiveTab('scanned')}
                >
                    Picked <span className="mpp-tab-badge mpp-tab-badge-picked">{pickedCount}</span>
                </button>
            </div>

            <div className="mpp-card-list">
                {visibleRows.length === 0 ? (
                    <div className="mpp-empty">
                        {activeTab === 'pending'
                            ? 'No pending items.'
                            : 'No items picked yet.'}
                    </div>
                ) : (
                    <>
                        {visibleRows.map((item) => (
                            <PickItemCard
                                key={item.OutbounditemIdList || item.listIndex}
                                item={item}
                                onPick={pickItem}
                                onCheck={handleCheckRecord}
                                onPreview={onPreviewItem}
                                hideSelection={activeTab === 'scanned'}
                            />
                        ))}
                        <div className="mpp-end-of-list">
                            ✓ No more records
                        </div>
                    </>
                )}
            </div>
        </main>
    );
};

MobilePickPlan.propTypes = {
    packingLabels: PropTypes.array.isRequired,
    scanInput: PropTypes.string,
    setScanInput: PropTypes.func.isRequired,
    scanInputRef: PropTypes.object,
    scanTimerRef: PropTypes.object,
    scanFeedback: PropTypes.object,
    processScan: PropTypes.func.isRequired,
    activeTab: PropTypes.string,
    setActiveTab: PropTypes.func.isRequired,
    handleCheckRecord: PropTypes.func.isRequired,
    handleMultiUpdate: PropTypes.func.isRequired,
    handleSectionPrintAllAction: PropTypes.func.isRequired,
    filterData: PropTypes.object.isRequired,
    handleDateChange: PropTypes.func.isRequired,
    pickItem: PropTypes.func.isRequired,
    onPreviewItem: PropTypes.func,
};

export default MobilePickPlan;
